'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, GitBranch, ScrollText, Settings, Users, Radio,
  ChevronsLeft, ChevronsRight, ShieldCheck, Users2, Tags, Mail, Calendar, Kanban,
  MessageSquare, Inbox, Flag, UserPlus, Bell,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth, PlanView } from '@/lib/auth-context';
import { authFetch } from '@/lib/authFetch';
import HeaderActions from '@/components/ui/HeaderActions';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';
const AGENT_LAST_SEEN_KEY = 'agent_messages_last_seen_main';

type NavItem = {
  href: string;
  label: string;
  icon: React.ElementType;
  plans: PlanView[];
  /** Optional gate key — when set, the item is only rendered if the
   *  matching runtime flag is true (e.g. Gmail connected + show_inbox). */
  gate?: 'email';
};

const mainNavItems: NavItem[] = [
  { href: '/operations', label: 'Dashboard',    icon: LayoutDashboard, plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  // /partner stays as a deep-link target (e.g. from referral emails) but no
  // longer has its own sidebar entry — the dashboard lives at
  // Settings → Affiliate now.
  { href: '/agent',      label: 'Agent',        icon: MessageSquare,   plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/assistant',  label: 'Leads',        icon: Inbox,           plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/pipeline',   label: 'Pipeline',     icon: Kanban,          plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/schedule',   label: 'Schedule',     icon: Calendar,        plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/workflows',  label: 'Workflows',    icon: GitBranch,       plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/hub',        label: 'Workflow Hub', icon: LayoutDashboard, plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/customers',  label: 'Customers',    icon: Users,           plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/widget',     label: 'Channels',     icon: Radio,           plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  { href: '/email',      label: 'Email',        icon: Mail,            plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'], gate: 'email' },
  { href: '/logs',       label: 'Logs',         icon: ScrollText,      plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
  // /email-templates is admin-only while the feature is being built.
  { href: '/email-templates', label: 'Templates', icon: Mail,         plans: ['admin'] },
  { href: '/settings',   label: 'Settings',     icon: Settings,        plans: ['starter', 'crewsync', 'pro', 'ai', 'admin'] },
];

const adminNavItems: NavItem[] = [
  { href: '/admin', label: 'Dashboard', icon: ShieldCheck, plans: ['admin'] },
  { href: '/admin/alerts', label: 'Alerts', icon: Bell, plans: ['admin'] },
  { href: '/admin/affiliates', label: 'Affiliates', icon: Tags, plans: ['admin'] },
  { href: '/admin/tenants', label: 'Tenants', icon: Users2, plans: ['admin'] },
  { href: '/admin/feature-flags', label: 'Feature Flags', icon: Flag, plans: ['admin'] },
  { href: '/admin/resend', label: 'Resend', icon: Mail, plans: ['admin'] },
  { href: '/admin/leads', label: 'Waitlist', icon: UserPlus, plans: ['admin'] },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [agentUnread, setAgentUnread] = useState(0);
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [alertsTopSeverity, setAlertsTopSeverity] = useState<'critical' | 'warning' | 'info' | null>(null);
  const [openBugCount, setOpenBugCount] = useState(0);
  const [showEmailNav, setShowEmailNav] = useState(false);
  const [emailUnread, setEmailUnread] = useState(0);
  const { isAdmin, viewingPlan } = useAuth();

  // Gate the Email nav entry on (1) Gmail connected and (2) operator's
  // show_inbox setting being true. Operators without Gmail connected (or
  // who toggle the inbox off in Settings → Email) shouldn't see a tab
  // that links to an empty page. Both endpoints are tenant-scoped.
  useEffect(() => {
    const ctrl = { cancelled: false };
    async function check() {
      try {
        const statusRes = await authFetch(`${API_URL}/api/connections/google/status`);
        if (!statusRes.ok || ctrl.cancelled) return;
        const status = await statusRes.json();
        if (status?.connected !== true) {
          if (!ctrl.cancelled) setShowEmailNav(false);
          return;
        }
        const settingsRes = await authFetch(`${API_URL}/api/gmail/settings`);
        if (!settingsRes.ok || ctrl.cancelled) return;
        const settings = await settingsRes.json();
        if (!ctrl.cancelled) setShowEmailNav(settings?.show_inbox === true);
      } catch { /* network */ }
    }
    check();
    return () => { ctrl.cancelled = true; };
  }, []);

  // When the user navigates to /agent, mark everything as seen
  useEffect(() => {
    if (pathname.startsWith('/agent')) {
      try { localStorage.setItem(AGENT_LAST_SEEN_KEY, new Date().toISOString()); }
      catch { /* private mode */ }
      setAgentUnread(0);
    }
  }, [pathname]);

  // Poll for unread agent messages every 30s. Skip while on /agent (already seen).
  useEffect(() => {
    if (pathname.startsWith('/agent')) return;
    let cancelled = false;

    async function check() {
      let lastSeen: string;
      try {
        lastSeen = localStorage.getItem(AGENT_LAST_SEEN_KEY) || new Date(0).toISOString();
      } catch {
        lastSeen = new Date(0).toISOString();
      }
      try {
        const res = await authFetch(`${API_URL}/api/agent-messages/since?thread=main&ts=${encodeURIComponent(lastSeen)}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const newAgentMsgs = (data.messages || []).filter((m: { role: string }) => m.role === 'agent');
        if (!cancelled) setAgentUnread(newAgentMsgs.length);
      } catch { /* network */ }
    }

    check();
    const interval = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pathname]);

  // Poll unacknowledged system alerts (admin only). Lights up the bell on
  // the Admin → Alerts nav item when the server fail-fast guard, the
  // out-of-process watchdog, or a customer bug report records a new fault.
  // See server/lib/system-alerts.js. Also tracks the highest-severity
  // unacknowledged alert so the Admin → Dashboard nav item can show a
  // colored dot indicating attention type:
  //   red  = critical (system fault, e.g. backend down)
  //   yellow = warning (customer bug report, etc.)
  //   green  = info (low-priority signals, e.g. support questions)
  useEffect(() => {
    if (!isAdmin || viewingPlan !== 'admin') {
      setAlertsUnread(0);
      setAlertsTopSeverity(null);
      return;
    }
    let cancelled = false;
    async function check() {
      try {
        // Pull up to 50 unack alerts so we can compute the top severity
        // without two round-trips. The Alerts page uses limit=100 anyway.
        const res = await authFetch(`${API_URL}/api/admin/system-alerts?unacknowledged=true&limit=50`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const count = Number(data.unacknowledged_count) || 0;
        const alerts: Array<{ severity?: string }> = Array.isArray(data.alerts) ? data.alerts : [];
        const hasCritical = alerts.some(a => a.severity === 'critical');
        const hasWarning = alerts.some(a => a.severity === 'warning');
        const hasInfo = alerts.some(a => a.severity === 'info');
        const top: 'critical' | 'warning' | 'info' | null =
          hasCritical ? 'critical' : hasWarning ? 'warning' : hasInfo ? 'info' : null;
        if (!cancelled) {
          setAlertsUnread(count);
          setAlertsTopSeverity(top);
        }
      } catch { /* network */ }
    }
    check();
    const interval = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pathname, isAdmin, viewingPlan]);

  // Poll open bug-report count separately. system_alerts only contains
  // user-submitted bug reports (we deliberately don't auto-alert on
  // agent_flag / workflow_error rows because those are noisy), but Chris
  // still wants the Admin → Dashboard nav item to light up when ANY
  // support item needs attention. So we OR the bug count with the
  // system_alerts count for the dot decision.
  useEffect(() => {
    if (!isAdmin || viewingPlan !== 'admin') { setOpenBugCount(0); return; }
    let cancelled = false;
    async function check() {
      try {
        const res = await authFetch(`${API_URL}/api/bug-reports/admin?status=open&limit=1`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const count = (data?.stats?.by_status?.open as number | undefined) ?? 0;
        if (!cancelled) setOpenBugCount(count);
      } catch { /* network */ }
    }
    check();
    const interval = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pathname, isAdmin, viewingPlan]);

  // Poll unread email count every 30s. Skip when on /email (already visible).
  // Only runs when the Email nav itself is shown (gate: 'email').
  useEffect(() => {
    if (!showEmailNav) { setEmailUnread(0); return; }
    if (pathname.startsWith('/email')) { setEmailUnread(0); return; }
    let cancelled = false;
    async function check() {
      try {
        const res = await authFetch(`${API_URL}/api/email/unread-count`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setEmailUnread(Number(data.count) || 0);
      } catch { /* network */ }
    }
    check();
    const interval = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pathname, showEmailNav]);

  // Hide sidebar entirely on workflow editor (it has its own layout)
  const isEditor = pathname.startsWith('/workflows/') && pathname !== '/workflows';

  if (isEditor) return null;

  const visibleMainNav = mainNavItems.filter(item => {
    if (!item.plans.includes(viewingPlan)) return false;
    if (item.gate === 'email' && !showEmailNav) return false;
    return true;
  });
  const showAdmin = isAdmin && viewingPlan === 'admin';

  return (
    <>
      {/* Mobile top strip — logo on the left, header actions (plan/theme/bell)
          on the right. Single header on mobile (the desktop Header below is
          hidden on mobile). Navigation lives in MobileTabBar at the bottom. */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-4 bg-[--bg-card]/95 backdrop-blur-xl z-30 border-b border-[--border-primary]">
        <img src="/crewsync-logo.png" alt="CrewSync" className="h-7" />
        <HeaderActions />
      </div>

      {/* Sidebar — desktop only. Mobile uses MobileTabBar instead. */}
      <aside className={clsx(
        'shrink-0 hidden md:flex flex-col bg-[--bg-card] z-40 transition-transform duration-300',
        'md:h-screen md:sticky md:top-0',
        collapsed ? 'md:w-16' : 'md:w-64',
      )}>
        {/* Logo + collapse - desktop only */}
        <div className={clsx(
          "hidden md:flex items-center",
          collapsed ? "justify-center p-3" : "justify-between px-6 py-4"
        )}>
          {/* Logo — only visible when expanded */}
          {!collapsed && (
            <img src="/crewsync-logo.png" alt="CrewSync" className="h-8" />
          )}
          {/* Collapse/expand toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md text-[--text-secondary] hover:bg-[--bg-main] hover:text-[--text-primary] transition-all"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {visibleMainNav.map(({ href, label, icon: Icon }) => {
            const active = href === '/' ? pathname === '/' : pathname.startsWith(href);
            const showAgentBadge = href === '/agent' && agentUnread > 0;
            const showEmailBadge = href === '/email' && emailUnread > 0;
            return (
              <Link
                key={href}
                href={href}
                className={clsx(
                  'relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150',
                  collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-3 md:py-2.5',
                  'min-h-[44px]',
                  active
                    ? 'bg-[#00C2A8]/10 text-[#00C2A8]'
                    : 'text-[--text-secondary] hover:bg-[--bg-main] hover:text-[--text-primary]'
                )}
              >
                <div className="relative shrink-0">
                  <Icon className="w-5 h-5 md:w-4 md:h-4" strokeWidth={active ? 2.5 : 2} />
                  {showAgentBadge && collapsed && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-[#00C2A8] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {agentUnread > 9 ? '9+' : agentUnread}
                    </span>
                  )}
                  {showEmailBadge && collapsed && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {emailUnread > 9 ? '9+' : emailUnread}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <>
                    <span className="flex-1">{label}</span>
                    {showAgentBadge && (
                      <span className="min-w-[18px] h-[18px] px-1.5 bg-[#00C2A8] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                        {agentUnread > 9 ? '9+' : agentUnread}
                      </span>
                    )}
                    {showEmailBadge && (
                      <span className="min-w-[18px] h-[18px] px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                        {emailUnread > 9 ? '9+' : emailUnread}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Admin section */}
        {showAdmin && (
          <div className="p-3 pt-5 space-y-0.5">
            {!collapsed && (
              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[--text-secondary] opacity-60">
                Admin
              </p>
            )}
            {adminNavItems.map(({ href, label, icon: Icon }) => {
              const active = href === '/admin' ? pathname === '/admin' : pathname.startsWith(href);
              const showAlertsBadge = href === '/admin/alerts' && alertsUnread > 0;
              // Small colored dot on the Admin → Dashboard nav item that
              // mirrors the highest-severity unacknowledged alert. Lets Chris
              // see at a glance that something needs attention without
              // navigating to /admin/alerts. Color = type:
              //   red    = critical (system fault)
              //   yellow = warning (customer bug report)
              //   green  = info (low-priority)
              // Admin → Dashboard nav item lights up when ANY admin
              // attention item exists: an unack system_alert OR an open
              // bug report (auto-detected counts too, since Chris wants
              // a single "something needs me" indicator). Color follows
              // highest signal: critical alert wins, then any warning
              // (incl. open bugs), then info.
              const hasOpenBugs = openBugCount > 0;
              const effectiveTopSeverity = alertsTopSeverity || (hasOpenBugs ? 'warning' : null);
              const showAdminDot = href === '/admin' && effectiveTopSeverity && pathname !== '/admin';
              const dotColor = effectiveTopSeverity === 'critical' ? 'bg-red-500'
                : effectiveTopSeverity === 'warning' ? 'bg-yellow-500'
                : effectiveTopSeverity === 'info' ? 'bg-green-500'
                : 'bg-transparent';
              return (
                <Link
                  key={href}
                  href={href}
                  className={clsx(
                    'relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150',
                    collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-3 md:py-2.5',
                    'min-h-[44px]',
                    active
                      ? 'bg-[#F59E0B]/10 text-[#F59E0B]'
                      : 'text-[--text-secondary] hover:bg-[--bg-main] hover:text-[--text-primary]'
                  )}
                >
                  <div className="relative shrink-0">
                    <Icon className="w-5 h-5 md:w-4 md:h-4" strokeWidth={active ? 2.5 : 2} />
                    {showAlertsBadge && collapsed && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                        {alertsUnread > 9 ? '9+' : alertsUnread}
                      </span>
                    )}
                    {showAdminDot && (
                      <span
                        className={clsx('absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-[--bg-card]', dotColor)}
                        title={(() => {
                          const parts: string[] = [];
                          if (alertsUnread > 0) parts.push(`${alertsUnread} unack alert${alertsUnread === 1 ? '' : 's'}`);
                          if (openBugCount > 0) parts.push(`${openBugCount} open bug${openBugCount === 1 ? '' : 's'}`);
                          return parts.join(' • ') || 'Open admin items';
                        })()}
                      />
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      <span className="flex-1">{label}</span>
                      {showAlertsBadge && (
                        <span className="min-w-[18px] h-[18px] px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                          {alertsUnread > 9 ? '9+' : alertsUnread}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </aside>
    </>
  );
}
